import { useState } from 'react';
import { Controller, FormProvider, UseFormReturn, get } from 'react-hook-form';
import { z } from 'zod';
import { ReactComponent as AccordionArrow } from '../icons/accordion-arrow.svg';
import { ReactComponent as CalculatorArrow } from '../icons/calculator-arrow.svg';
import { OnboardingFinancesFormShape } from '../../utils/onboarding';
import { formatCurrency, useZodForm } from '../../utils';
import { InputRow, MoneyInput } from '../forms';

function safeSum(values: (number | undefined | null)[]): number {
  const filtered: number[] = values.filter(Boolean) as number[];
  return filtered.reduce(
    (accumulator, currentValue) => accumulator + currentValue,
    0
  );
}

export const expandedLiabilitiesFormSchema = z.object({
  studentLoans: z.number().min(0),
  autoLoans: z.number().min(0),
  personalLoans: z.number().min(0),
  retainedExpenses: z.number().min(0),
});

export const ExpandedLiabilitiesForm: React.FC<{
  methods: UseFormReturn<OnboardingFinancesFormShape>;
}> = ({ methods: topLevelMethods }) => {
  const [expanded, setExpanded] = useState(false);
  const methods = useZodForm({ schema: expandedLiabilitiesFormSchema });

  const watched = methods.watch([
    'studentLoans',
    'autoLoans',
    'personalLoans',
    'retainedExpenses',
  ]);

  const monthlyLiabilities = topLevelMethods.watch('monthlyLiabilities');
  const calculatedSum = safeSum(watched);

  if (expanded) {
    return (
      <div className="border-hardwood-400 bg-hardwood-50 relative mt-[32px] w-full rounded-[8px] border-[1px] p-[24px]">
        <AccordionArrow
          className="text-hardwood-400 absolute right-[14px] top-[11px] h-[20px] w-[20px] cursor-pointer"
          onClick={() => setExpanded(false)}
        />
        <div className="typo-body text-[16px] font-bold">
          Monthly liabilities
        </div>

        <FormProvider {...methods}>
          <form className="w-full">
            <div className="flex gap-4">
              <div className="mt-4 flex-1">
                <Controller
                  control={methods.control}
                  name="studentLoans"
                  render={({ field }) => {
                    return (
                      <InputRow label="Student loans">
                        <MoneyInput {...field} />
                      </InputRow>
                    );
                  }}
                />
                <div className="mt-4">
                  <Controller
                    control={methods.control}
                    name="autoLoans"
                    render={({ field }) => {
                      return (
                        <InputRow label="Auto loans">
                          <MoneyInput {...field} />
                        </InputRow>
                      );
                    }}
                  />
                </div>
              </div>
              <div className="mt-10 flex-1">
                <div className="typo-body-small">
                  Enter your liabilities using these common types. While you may
                  have other debt, these types impact your mortgage eligibility
                  the most.
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="mt-4 flex-1">
                <Controller
                  control={methods.control}
                  name="personalLoans"
                  render={({ field }) => {
                    return (
                      <InputRow label="Personal loans">
                        <MoneyInput {...field} />
                      </InputRow>
                    );
                  }}
                />
              </div>
              <div className="flex-1"></div>
            </div>

            <div className="flex gap-4">
              <div className="mt-4 flex-1">
                <Controller
                  control={methods.control}
                  name="retainedExpenses"
                  render={({ field }) => {
                    return (
                      <InputRow label="Expenses for homes you’ll keep">
                        <MoneyInput {...field} />
                      </InputRow>
                    );
                  }}
                />
              </div>
              <div className="mt-12 flex-1">
                <div className="typo-body-small">
                  Include the mortgage or rent, taxes, insurance, dues, etc.
                </div>
              </div>
            </div>

            <div className="mt-[24px] flex gap-4">
              <div className="flex-1">
                <div className="flex flex-col">
                  <div className="typo-body bg-hardwood-200 flex h-[40px] items-center justify-center rounded-tl-[8px] rounded-tr-[8px] text-center font-bold">
                    Total: {formatCurrency(calculatedSum)}
                    /mo
                  </div>
                  <div
                    className="typo-button bg-morton-600 hover:bg-morton-700 flex h-[40px] cursor-pointer items-center justify-center rounded-bl-[8px]  rounded-br-[8px] text-center text-white"
                    onClick={() => {
                      topLevelMethods.setValue(
                        'monthlyLiabilities',
                        calculatedSum
                      );
                    }}
                  >
                    USE THIS TOTAL
                  </div>
                </div>
                <div className="flex justify-center overflow-hidden">
                  <CalculatorArrow className="mt-[-6px]" />
                </div>
              </div>
              <div className="flex-1">
                <div className="typo-body-small">
                  You should likely use this sum as your total monthly
                  liabilities, but you can enter a different number, if you
                  prefer.
                </div>
              </div>
            </div>

            <div className="mt-4 flex gap-4">
              <div className="flex-1">
                <Controller
                  control={topLevelMethods.control}
                  name="monthlyLiabilities"
                  render={({ field }) => {
                    return (
                      <InputRow label="Your total monthly liabilities">
                        <MoneyInput
                          {...field}
                          fieldErrorOverride={get(
                            topLevelMethods.formState.errors,
                            'monthlyLiabilities'
                          )}
                        />
                      </InputRow>
                    );
                  }}
                />
              </div>

              <div className="flex-1"></div>
            </div>
          </form>
        </FormProvider>
      </div>
    );
  }

  return (
    <div className="mt-[32px] flex w-full max-w-[220px] flex-col items-start justify-center gap-4">
      <Controller
        control={topLevelMethods.control}
        name="monthlyLiabilities"
        render={({ field }) => {
          return (
            <InputRow label="Monthly liabilities">
              <MoneyInput
                {...field}
                fieldErrorOverride={get(
                  topLevelMethods.formState.errors,
                  'monthlyLiabilities'
                )}
                rightIcon={
                  <div className="typo-body-small text-sidewalk-500">/mo</div>
                }
              />
            </InputRow>
          );
        }}
      />

      <div className="w-full">
        <div className="typo-body-small text-hardwood-700">
          Not sure what debts to include?
        </div>

        <a
          onClick={() => setExpanded(true)}
          className="typo-body-small text-morton-600 flex cursor-pointer items-center font-bold hover:underline"
        >
          Calculate your liabilities.
          <AccordionArrow className="text-morton-600 h-[20px] w-[20px] rotate-180" />
        </a>
      </div>
    </div>
  );
};
